import React, { useState, useEffect } from 'react';
import { Archive, Clock, Plus, Search, Filter, Edit, Calendar, X, Trash2, UserPlus, PlusCircle, MinusCircle } from 'lucide-react';
import { fetchCustomers, createCustomer, updateCustomer } from '../lib/api';
import type { Customer, Contact } from '../lib/types';
import { OrderHistory } from './OrderHistory';

const DAYS_OF_WEEK = [
  'Select a Day',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday'
];

const emptyContact: Omit<Contact, 'id'> = {
  firstName: '',
  lastName: '',
  position: '',
  email: '',
  phone: '',
  mobile: '',
  isPrimary: false
};

const emptyPricingTier = {
  minQuantity: 0,
  discountPercentage: 0
};

const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return 'Not set';
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  });
};

const Customers: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [formData, setFormData] = useState<Partial<Customer>>({
    companyName: '',
    contacts: [{ ...emptyContact, isPrimary: true }],
    address: {
      street: '',
      city: '',
      state: '',
      postcode: '',
    },
    status: 'active',
    archived: false,
    pricingTiers: [],
    weeklyOrder: {
      enabled: false,
      dayOfWeek: 1,
      time: '09:00'
    },
    creditLimit: null,
    paymentTerms: '',
    licenseNumber: '',
    taxId: '',
    notes: ''
  });
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showOrderHistory, setShowOrderHistory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterArchived, setFilterArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const data = await fetchCustomers();
      setCustomers(data);
      setError(null);
    } catch (err) {
      setError('Failed to load customers');
      console.error('Error loading customers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      if (editingCustomer) {
        await updateCustomer({
          ...editingCustomer,
          ...formData as Customer
        });
      } else {
        await createCustomer(formData as Omit<Customer, 'id' | 'createdAt'>);
      }
      
      await loadCustomers();
      setShowForm(false);
      setEditingCustomer(null);
      setFormData({
        companyName: '',
        contacts: [{ ...emptyContact, isPrimary: true }],
        address: { street: '', city: '', state: '', postcode: '' },
        status: 'active',
        archived: false,
        pricingTiers: [],
        weeklyOrder: { enabled: false, dayOfWeek: 1, time: '09:00' },
        creditLimit: null,
        paymentTerms: '',
        licenseNumber: '',
        taxId: '',
        notes: ''
      });
    } catch (err) {
      setError(editingCustomer ? 'Failed to update customer' : 'Failed to create customer');
      console.error('Error saving customer:', err);
    }
  };

  const handleAddContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [...(prev.contacts || []), { ...emptyContact }]
    }));
  };

  const handleRemoveContact = (index: number) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts?.filter((_, i) => i !== index)
    }));
  };

  const handleContactChange = (index: number, field: keyof Contact, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts?.map((contact, i) => {
        if (i === index) {
          return { ...contact, [field]: value };
        }
        if (field === 'isPrimary' && value === true) {
          return { ...contact, isPrimary: false };
        }
        return contact;
      })
    }));
  };

  const handleAddPricingTier = () => {
    setFormData(prev => ({
      ...prev,
      pricingTiers: [...(prev.pricingTiers || []), { ...emptyPricingTier }]
    }));
  };

  const handleRemovePricingTier = (index: number) => {
    setFormData(prev => ({
      ...prev,
      pricingTiers: prev.pricingTiers?.filter((_, i) => i !== index)
    }));
  };

  const handlePricingTierChange = (index: number, field: keyof typeof emptyPricingTier, value: number) => {
    setFormData(prev => ({
      ...prev,
      pricingTiers: prev.pricingTiers?.map((tier, i) => 
        i === index ? { ...tier, [field]: value } : tier
      )
    }));
  };

  const filteredCustomers = customers
    .filter(customer => {
      const matches = customer.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.contacts?.some(contact => 
          `${contact.firstName} ${contact.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
        ) ||
        customer.licenseNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.taxId?.toLowerCase().includes(searchTerm.toLowerCase());
      return matches && (filterArchived ? customer.archived : true);
    })
    .sort((a, b) => a.companyName.localeCompare(b.companyName));

  const getPrimaryContact = (customer: Customer) => {
    return customer.contacts?.find(contact => contact.isPrimary);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
        <button
          onClick={() => {
            setEditingCustomer(null);
            setFormData({
              companyName: '',
              contacts: [{ ...emptyContact, isPrimary: true }],
              address: { street: '', city: '', state: '', postcode: '' },
              status: 'active',
              archived: false,
              pricingTiers: [],
              weeklyOrder: { enabled: false, dayOfWeek: 1, time: '09:00' },
              creditLimit: null,
              paymentTerms: '',
              licenseNumber: '',
              taxId: '',
              notes: ''
            });
            setShowForm(true);
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
        >
          <Plus size={20} />
          Add Customer
        </button>
      </div>

      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
          <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
        </div>
        <button
          onClick={() => setFilterArchived(!filterArchived)}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
            filterArchived
              ? 'bg-gray-200 text-gray-700'
              : 'bg-white border border-gray-300 text-gray-600'
          }`}
        >
          <Filter size={20} />
          {filterArchived ? 'Show All' : 'Show Active'}
        </button>
      </div>

      {error && <div className="text-red-600 mb-4 p-3 bg-red-50 rounded">{error}</div>}

      {loading ? (
        <div className="text-center py-8">Loading customers...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Primary Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Weekly Order
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCustomers.map((customer) => {
                const primaryContact = getPrimaryContact(customer);
                const hasRequiredInfo = customer.licenseNumber && customer.taxId;
                
                return (
                  <tr key={customer.id} className={`${hasRequiredInfo ? 'bg-green-50' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {customer.companyName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {customer.address?.city}, {customer.address?.state}
                      </div>
                      {customer.licenseNumber && (
                        <div className="text-sm text-gray-500">
                          License: {customer.licenseNumber}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {primaryContact ? `${primaryContact.firstName} ${primaryContact.lastName}` : 'No primary contact'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {primaryContact?.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          customer.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {customer.status}
                      </span>
                      {customer.creditLimit && (
                        <div className="text-sm text-gray-500 mt-1">
                          Credit Limit: {formatCurrency(customer.creditLimit)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {customer.weeklyOrder?.enabled ? (
                        <div className="text-sm text-gray-900">
                          <span className="flex items-center gap-1">
                            <Calendar size={16} />
                            {DAYS_OF_WEEK[customer.weeklyOrder.dayOfWeek]}s at{' '}
                            {customer.weeklyOrder.time}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">Not scheduled</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setShowOrderHistory(customer.id)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Clock size={20} />
                        </button>
                        <button
                          onClick={() => {
                            setEditingCustomer(customer);
                            setFormData(customer);
                            setShowForm(true);
                          }}
                          className="text-green-600 hover:text-green-900"
                        >
                          <Edit size={20} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingCustomer(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Company Information</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company Name</label>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                      className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">License Number</label>
                      <input
                        type="text"
                        value={formData.licenseNumber}
                        onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                        className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Tax ID</label>
                      <input
                        type="text"
                        value={formData.taxId}
                        onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                        className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Credit Limit</label>
                      <input
                        type="number"
                        value={formData.creditLimit || ''}
                        onChange={(e) => setFormData({ ...formData, creditLimit: e.target.value ? parseFloat(e.target.value) : null })}
                        className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                        placeholder="Enter credit limit"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Payment Terms</label>
                      <input
                        type="text"
                        value={formData.paymentTerms}
                        onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
                        className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                        placeholder="e.g., Net 30"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                      className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                      rows={4}
                      placeholder="Enter any additional notes..."
                    />
                  </div>
                </div>
              </div>

              {/* Contacts */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Contacts</h3>
                  <button
                    type="button"
                    onClick={handleAddContact}
                    className="text-green-600 hover:text-green-700 flex items-center gap-2"
                  >
                    <UserPlus size={20} />
                    Add Contact
                  </button>
                </div>
                <div className="space-y-6">
                  {formData.contacts?.map((contact, index) => (
                    <div key={index} className="border rounded-lg p-4 relative">
                      <div className="absolute top-4 right-4 flex items-center gap-4">
                        {formData.contacts!.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveContact(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 size={20} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">First Name</label>
                          <input
                            type="text"
                            value={contact.firstName}
                            onChange={(e) => handleContactChange(index, 'firstName', e.target.value)}
                            className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Last Name</label>
                          <input
                            type="text"
                            value={contact.lastName}
                            onChange={(e) => handleContactChange(index, 'lastName', e.target.value)}
                            className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                            required
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700">Position</label>
                          <input
                            type="text"
                            value={contact.position}
                            onChange={(e) => handleContactChange(index, 'position', e.target.value)}
                            className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Email</label>
                          <input
                            type="email"
                            value={contact.email}
                            onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                            className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Phone</label>
                          <input
                            type="tel"
                            value={contact.phone}
                            onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                            className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Mobile (Optional)</label>
                          <input
                            type="tel"
                            value={contact.mobile}
                            onChange={(e) => handleContactChange(index, 'mobile', e.target.value)}
                            className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={contact.isPrimary}
                              onChange={(e) => handleContactChange(index, 'isPrimary', e.target.checked)}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-900">Primary Contact</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Address</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Street</label>
                    <input
                      type="text"
                      value={formData.address?.street}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address!, street: e.target.value }
                      })}
                      className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <input
                      type="text"
                      value={formData.address?.city}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address!, city: e.target.value }
                      })}
                      className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">State</label>
                    <input
                      type="text"
                      value={formData.address?.state}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address!, state: e.target.value }
                      })}
                      className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                    <input
                      type="text"
                      value={formData.address?.postcode}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address!, postcode: e.target.value }
                      })}
                      className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                </div>
              </div>

              {/* Pricing Tiers */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Pricing Tiers</h3>
                  <button
                    type="button"
                    onClick={handleAddPricingTier}
                    className="text-green-600 hover:text-green-700 flex items-center gap-2"
                  >
                    <PlusCircle size={20} />
                    Add Tier
                  </button>
                </div>
                <div className="space-y-4">
                  {formData.pricingTiers?.map((tier, index) => (
                    <div key={index} className="flex items-end gap-4 border rounded-lg p-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Minimum Quantity
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={tier.minQuantity}
                          onChange={(e) => handlePricingTierChange(index, 'minQuantity', parseInt(e.target.value))}
                          className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                          required
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Discount Percentage
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={tier.discountPercentage}
                          onChange={(e) => handlePricingTierChange(index, 'discountPercentage', parseInt(e.target.value))}
                          className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                          required
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemovePricingTier(index)}
                        className="text-red-600 hover:text-red-700 p-2"
                      >
                        <MinusCircle size={20} />
                      </button>
                    </div>
                  ))}
                  {formData.pricingTiers?.length === 0 && (
                    <p className="text-sm text-gray-500 italic">No pricing tiers configured</p>
                  )}
                </div>
              </div>

              {/* Weekly Order Schedule */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Weekly Order Schedule</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.weeklyOrder?.enabled}
                      onChange={(e) => setFormData({
                        ...formData,
                        weeklyOrder: {
                          ...formData.weeklyOrder!,
                          enabled: e.target.checked,
                          dayOfWeek: 0,
                          time: '09:00'
                        }
                      })}
                      className="h-4 w-4 text-green-600 rounded border-gray-300"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Enable Weekly Orders
                    </label>
                  </div>
                  
                  {formData.weeklyOrder?.enabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Day of Week</label>
                        <select
                          value={formData.weeklyOrder?.dayOfWeek}
                          onChange={(e) => setFormData({
                            ...formData,
                            weeklyOrder: {
                              ...formData.weeklyOrder!,
                              dayOfWeek: parseInt(e.target.value)
                            }
                          })}
                          className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                          required
                        >
                          {DAYS_OF_WEEK.map((day, index) => (
                            <option key={day} value={index} disabled={index === 0}>
                              {day}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Time</label>
                        <input
                          type="time"
                          value={formData.weeklyOrder?.time}
                          onChange={(e) => setFormData({
                            ...formData,
                            weeklyOrder: {
                              ...formData.weeklyOrder!,
                              time: e.target.value
                            }
                          })}
                          className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                          required={formData.weeklyOrder?.enabled}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingCustomer(null);
                  }}
                  className="px-4 py-2 border rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formData.weeklyOrder?.enabled && formData.weeklyOrder.dayOfWeek === 0}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingCustomer ? 'Update Customer' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showOrderHistory && (
        <OrderHistory
          customerId={showOrderHistory}
          customer={customers.find(c => c.id === showOrderHistory)}
          onClose={() => setShowOrderHistory(null)}
        />
      )}
    </div>
  );
};

export default Customers;